import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import VisibilityIcon from "@mui/icons-material/Visibility";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can } from "@src/security/permissions";

import {
  DIAGNOSTICS_LIST_FACETS,
  normalizeDiagnosticsListFilters,
  isDefaultDiagnosticsListFilters
} from "./DiagnosticsListFacets";

import styles from "./DiagnosticsList.module.styl";

const LIST_DEFAULT_PAGE_SIZE = 50;

let initialRange = null;
let initialRangeUsesLeft = 0;

const readRangeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : LIST_DEFAULT_PAGE_SIZE;
  const safeOffset = Number.isFinite(offset) && offset >= 0 ? Math.floor(offset) : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange: params.has("offset") || params.has("limit")
  };
};

const writeRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));
  window.history.replaceState({}, "", url.toString());
};

const getEffectiveQuery = (query) => {
  const search = String(query?.search || "").trim();
  const sort = Array.isArray(query?.sort) ? query.sort : [];
  const filters = normalizeDiagnosticsListFilters(query?.filters || {});
  const hasActiveFilters = !isDefaultDiagnosticsListFilters(filters);
  const page = Number.isFinite(query?.page) ? query.page : 0;
  const pageSize = Number.isFinite(query?.pageSize) ? query.pageSize : LIST_DEFAULT_PAGE_SIZE;

  if (
    initialRange &&
    initialRange.hasExplicitRange &&
    initialRangeUsesLeft > 0 &&
    !search &&
    !hasActiveFilters &&
    page === 0 &&
    pageSize === LIST_DEFAULT_PAGE_SIZE
  ) {
    initialRangeUsesLeft -= 1;

    return {
      page: initialRange.page,
      pageSize: initialRange.pageSize,
      offset: initialRange.offset,
      limit: initialRange.limit,
      search,
      sort,
      filters
    };
  }

  initialRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort,
    filters
  };
};

const selectorsForSortItem = (sortItem) => {
  const column = sortItem?.column;

  if (Array.isArray(column?.sortSelectors)) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [column.sortSelector];
  }

  if (sortItem?.field) {
    return [sortItem.field];
  }

  return [];
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    if (sortItem?.direction !== "asc" && sortItem?.direction !== "desc") {
      continue;
    }

    for (const selector of selectorsForSortItem(sortItem)) {
      if (selector) {
        out.push({ selector, direction: sortItem.direction });
      }
    }
  }

  return out;
};

const buildPayload = (range) => {
  const sort = buildSortPayload(range.sort);
  const effectiveSort = sort.length ? sort : [{ selector: "generatedAt", direction: "desc" }];

  return {
    search: String(range.search || "").trim(),
    filters: normalizeDiagnosticsListFilters(range.filters),
    limit: range.limit,
    offset: range.offset,
    sort: effectiveSort
  };
};

const parseJsonIfPossible = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch (_e) {
    return value;
  }
};

const asPlainObject = (value) => {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
};

const getReportObject = (diagnosticOrReport) => {
  const value = parseJsonIfPossible(diagnosticOrReport?.report ?? diagnosticOrReport);
  return asPlainObject(value);
};

const getReportText = (diagnosticOrReport) => {
  const value = parseJsonIfPossible(diagnosticOrReport?.report ?? diagnosticOrReport);

  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object") {
    if (typeof value.text === "string") {
      return value.text;
    }

    if (typeof value.report === "string") {
      return value.report;
    }

    return JSON.stringify(value, null, 2);
  }

  return "";
};

const lineValue = (report, label) => {
  const text = getReportText(report);
  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const equalsPattern = new RegExp(`^${escapedLabel}\\s*=\\s*(.*)$`, "mi");
  const equalsMatch = text.match(equalsPattern);
  if (equalsMatch) {
    return equalsMatch[1].trim();
  }

  const colonPattern = new RegExp(`^${escapedLabel}\\s*:\\s*(.*)$`, "mi");
  const colonMatch = text.match(colonPattern);
  return colonMatch ? colonMatch[1].trim() : "";
};

const generatedAt = (row) => row.generatedAt || getReportObject(row)?.generatedAt || lineValue(row, "generatedAt");

const eventType = (row) => row.eventType || getReportObject(row)?.eventType || getReportObject(row)?.type || "diagnostic";

const tabletId = (row) => row.deviceId || getReportObject(row)?.deviceId || lineValue(row, "hardwareId");

const versionName = (row) => row.versionName || getReportObject(row)?.versionName || lineValue(row, "versionName");

const message = (row) => row.message || getReportObject(row)?.message || lineValue(row, "message");

const copyText = async (text) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(String(text || ""));
  }
};

const enrichDiagnosticsRows = (rows) => {
  return (rows || []).map((row) => ({
    ...row,
    generatedAt: generatedAt(row),
    eventType: eventType(row),
    deviceId: tabletId(row),
    versionName: versionName(row),
    message: message(row),
    reportText: getReportText(row)
  }));
};

const diagnosticsListProjection = {
  storageKey: "haiti.diagnostics.table.v6",
  facets: DIAGNOSTICS_LIST_FACETS,

  columns: [
    {
      title: "common.generated",
      field: "generatedAt",
      sortSelector: "generatedAt",
      filtering: false,
      render: (row) => row.generatedAt || null
    },

    {
      title: "common.event",
      field: "eventType",
      sortSelector: "eventType",
      filtering: false,
      render: (row) => row.eventType || null
    },

    {
      title: "common.tabletId",
      field: "deviceId",
      sortSelector: "deviceId",
      filtering: false,
      render: (row) => row.deviceId || null
    },

    {
      title: "common.appVersion",
      field: "versionName",
      sortSelector: "versionName",
      filtering: false,
      render: (row) => row.versionName || null
    },

    {
      title: "common.message",
      field: "message",
      sortSelector: "message",
      filtering: false,
      render: (row) => (
        <span className={styles.previewText}>{row.message || null}</span>
      )
    },

    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions: (row, gui) => (
    <>
      {can(gui, "READ_DIAGNOSTICS") ? (
        <Tooltip title={t("diagnostics.view")}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              if (row.id) {
                gui.goTo(`/diagnostics/${row.id}`);
              }
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}

      <Tooltip title={t("diagnostics.copyReport")}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            copyText(row.reportText);
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  ),

  fetch: async (query) => {
    const range = getEffectiveQuery(query);

    writeRangeToUrl({ offset: range.offset, limit: range.limit });

    const payload = buildPayload(range);

    const diagnostics = await rest.request(getServiceUri() + "diagnostics/table", "POST", payload);
    const totalCount = await rest.request(getServiceUri() + "diagnostics/table/count", "POST", payload);

    return {
      data: enrichDiagnosticsRows(diagnostics || []),
      totalCount,
      page: range.page
    };
  }
};

export const displayDiagnosticsList = (gui) => {
  initialRange = readRangeFromUrl();
  initialRangeUsesLeft = initialRange.hasExplicitRange ? 3 : 0;

  return displayList(gui, diagnosticsListProjection);
};

export default displayDiagnosticsList;
